import { Dialog, DialogContent, DialogTitle, List, ListItem } from '@mui/material';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { isSameDay } from 'date-fns';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMonthlyRentalTypesQuery } from '../../../../hooks/queries/useRentalsQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { MonthlyRentalType } from '../../../../services/apiSLTP/materials/rentalTypes';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { formatDate } from '../../../../utils/dates';
import { crudStatus, getMaterialStateInfos, permissionLevels } from '../../../../utils/enums';
import StateCell from '../../../cells/StateCell';
import Icon from '../../../Icon';
import GenericAgGridTable from '../../GenericAgGridTable';
import { getColorByPercentage, getMaterialStateColor } from '../../../../utils/colors';
import { AgBarSeriesItemStylerParams, AgBarSeriesStyle, AgSparklineOptions } from 'ag-charts-community';
import Holidays from 'date-holidays';
import IconButton from '../../../shared/IconButton';
import { useTheme } from '../../../../hooks/useTheme';
import dayjs from 'dayjs';
const hd = new Holidays('FR');

interface MonthlyRentalTypesTableProps {
    currentDate: Date;
}

const MonthlyRentalTypesTable: React.FC<MonthlyRentalTypesTableProps> = ({ currentDate }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { muiTheme, mode } = useTheme();
    const [selectedRentalType, setSelectedRentalType] = useState<MonthlyRentalType['dailyRentalTypesByDate'][number]['daily'] | null>(null);

    const { data: dataMonthlyRentalTypes, isLoading: isMonthlyRentalTypesLoading } = useMonthlyRentalTypesQuery({
        params: { date: currentDate.toISOString() },
    });

    const daysInMonth = useMemo(() => {
        const days = [];
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const numDays = new Date(year, month + 1, 0).getDate();

        for (let day = 1; day <= numDays; day++) {
            const d = new Date(year, month, day);
            days.push({
                date: d,
                label: d.getDate().toString(),
            });
        }
        return days;
    }, [currentDate]);

    const rowData = useMemo(() => {
        if (!dataMonthlyRentalTypes) return [];

        return dataMonthlyRentalTypes.sort((a, b) => a.designation.localeCompare(b.designation));
    }, [dataMonthlyRentalTypes, daysInMonth]);

    const itemStyler: (params: AgBarSeriesItemStylerParams<any>) => AgBarSeriesStyle = (params) => {
        const max = Math.max(...rowData.map((value) => value.dailyRentalTypesByDate.length));

        const color = getColorByPercentage(params.yValue ?? 0, max);
        return { fill: color };
    };

    const columnDefs: ColDef[] = useMemo(() => {
        const baseCols: ColDef<MonthlyRentalType>[] = [
            {
                headerName: capitalizeFirstLetter(t('name')),
                field: 'designation',
                colId: 'name',
                pinned: 'left',
                initialWidth: 300,
                cellStyle: (params) =>
                    params.data?.crudStatus === crudStatus.DELETED
                        ? { color: muiTheme.palette.error.main }
                        : params.data?.crudStatus === crudStatus.INACTIVE
                          ? { color: muiTheme.palette.action.disabled }
                          : undefined,
            },
            {
                headerName: capitalizeFirstLetter(t('codeProC')),
                field: 'codeProC',
                colId: 'codeProC',
                pinned: 'left',
                initialWidth: 120,
            },

            {
                headerName: capitalizeFirstLetter(t('prodDays')),
                colId: 'totalparkline',
                cellRenderer: 'agSparklineCellRenderer',
                pinned: 'left',
                valueGetter: (params) => {
                    return [params.data?.dailyRentalTypesByDate.length];
                },
                comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
                    return valueA - valueB;
                },
                cellRendererParams: {
                    sparklineOptions: {
                        type: 'bar',
                        direction: 'horizontal',
                        min: 0,
                        max: Math.max(...rowData.map((value) => value.dailyRentalTypesByDate.length)) + 5,

                        label: {
                            enabled: true,
                            placement: 'outside-end',
                            color: mode === 'dark' ? '#fff' : '#000',
                        },

                        itemStyler: itemStyler,
                    } as AgSparklineOptions,
                },
            },
        ];

        const dateCols: ColDef<MonthlyRentalType>[] = daysInMonth.map(({ date, label }) => ({
            headerName: label,
            colId: label,
            initialWidth: 120,
            valueGetter: (params) => params.data?.dailyRentalTypesByDate.find((value) => isSameDay(new Date(value.date), date))?.daily.length,
            cellRenderer: (params: ICellRendererParams<MonthlyRentalType>) => {
                const currentRentalTypeDay = params.data?.dailyRentalTypesByDate.find((value) => isSameDay(new Date(value.date), date));
                return currentRentalTypeDay ? (
                    <div className="fx-r fx-items-c gap-5px text-grey">
                        <StateCell text={currentRentalTypeDay.daily.length.toString()} stateColor={''} showDot={false} />
                        {!!currentRentalTypeDay.daily.length && (
                            <IconButton iconName="infoSharp" onClick={() => setSelectedRentalType(currentRentalTypeDay.daily)} />
                        )}
                    </div>
                ) : (
                    <div className=" text-gray">{t('n/a').toUpperCase()}</div>
                );
            },
            headerStyle: {
                color: hd.isHoliday(date)
                    ? muiTheme.palette.error.main
                    : date.getDay() === 6
                      ? muiTheme.palette.warning.main
                      : date.getDay() === 0
                        ? muiTheme.palette.error.main
                        : '',
            },
            suppressFloatingFilterButton: true,
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
        }));

        const adminCols: ColDef<MonthlyRentalType>[] =
            user?.profileId.permsLevel === permissionLevels.ADMIN
                ? [
                      {
                          headerName: '_id',
                          field: '_id',
                          colId: '_id',
                          initialWidth: 250,
                          cellClass: 'usage-col-main',
                          headerClass: 'usage-col-main',
                      },
                  ]
                : [];

        return [...baseCols, ...dateCols, ...adminCols];
    }, [t, daysInMonth, user, rowData, mode]);

    return (
        <>
            <GenericAgGridTable
                rowData={rowData}
                columnDefs={columnDefs}
                exportCsvFileName={`presence-rentals-${dayjs(currentDate).format('YYYY-MM')}`}
                agGridProps={{ getRowId: ({ data }) => data._id }}
                loading={isMonthlyRentalTypesLoading}
            />
            <Dialog open={!!selectedRentalType} onClose={() => setSelectedRentalType(null)}>
                <DialogTitle>{capitalizeFirstLetter(t('sites'))}</DialogTitle>
                <DialogContent>
                    <List>
                        {selectedRentalType?.length ? (
                            selectedRentalType?.map((daily, i) => (
                                <ListItem key={i} className="gap-5px fx-r">
                                    {daily.dailyRentalType.designation}
                                    <span>{'-'}</span>
                                    <span className="font-w-700">{daily?.site.name}</span>
                                    <span>{'-'}</span>
                                    <span style={{ color: getMaterialStateColor(daily.dailyRentalType.state) }}>
                                        {capitalizeFirstLetter(t(getMaterialStateInfos(daily.dailyRentalType.state)))}
                                    </span>
                                    <span>{'-'}</span>
                                    {capitalizeFirstLetter(daily.dailyRentalType.isUsed ? t('used') : t('notUsed'))}
                                </ListItem>
                            ))
                        ) : (
                            <ListItem>{t('noSite')}</ListItem>
                        )}
                    </List>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default MonthlyRentalTypesTable;
